import { fetchAuthSession } from "aws-amplify/auth";
import axios, { AxiosResponse } from "axios";
import { sessionStorageKeys } from "../hooks/useSelectedServiceCenter";
import { env } from "./env";

export function createHttpInstance(baseUrl: string = env.VITE_API_URL) {
  const baseConfig = { baseURL: baseUrl };
  const instance = axios.create(baseConfig);

  instance.interceptors.request.use(async (request) => {
    // add auth header with jwt if account is logged in and request is to the api url
    const session = await fetchAuthSession();

    request.headers.Authorization = `Bearer ${session.tokens?.accessToken.toString()}`;
    // Only set Content-Type to application/json if it's not FormData
    // FormData needs to set its own Content-Type with boundary
    if (!(request.data instanceof FormData)) {
      request.headers["Content-type"] = "application/json";
    }

    const serviceCenter = JSON.parse(
      sessionStorage.getItem(sessionStorageKeys.currentTabServiceCenter) ?? "{}"
    );
    request.headers["X-Service-Center-ID"] = serviceCenter.id;
    request.headers["TimeZone"] =
      Intl.DateTimeFormat().resolvedOptions().timeZone;

    return request;
  });

  instance.interceptors.response.use((response: AxiosResponse) => {
    handleDates(response.data);
    return response;
  });

  return instance;
}

const isoDateFormat =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d*)?(?:[-+]\d{2}:?\d{2}|Z)?$/;

const dateOnlyFormat =
  /^[1-9][0-9]{3}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;

const isIsoDateString = (value: string): boolean => isoDateFormat.test(value);

const isDateOnlyString = (value: string): boolean => dateOnlyFormat.test(value);

export const handleDates = (body: any) => {
  if (body === null || body === undefined || typeof body !== "object") {
    return body;
  }

  for (const key of Object.keys(body)) {
    const value = body[key];
    if (typeof value === "string") {
      if (isIsoDateString(value)) {
        body[key] = new Date(value);
      }

      if (isDateOnlyString(value)) {
        const [year, month, day] = value.split("-").map(Number);

        body[key] = new Date(Date.UTC(year, month - 1, day));
      }
    } else if (typeof value === "object") handleDates(value);
  }
};

export const http = createHttpInstance(env.VITE_API_URL);
